import { useMutation, UseMutationResult } from '@tanstack/react-query'
import braintree, { BraintreeError } from 'braintree-web'

const CLIENT_AUTHORIZATION = process.env.NEXT_PUBLIC_BRAINTREE_CLIENT_AUTHORIZATION || ''

export interface CreditCardPayload {
  number: string
  expirationDate: string
  cvv: string
  billingAddress?: {
    postalCode: string
  }
}

type ClientRequest = (options: {
  endpoint: string
  method: string
  data: { creditCard: CreditCardPayload }
}) => Promise<ClientResponse>

interface ClientResponse {
  creditCards: { nonce: string; binData: { prepaid: string } }[]
}

/**
 * Do not call the api again if data hasn't changed
 * @return {boolean}
 */
export const arePayloadsEqual = (a: CreditCardPayload, b: CreditCardPayload): boolean =>
  a.number === b.number &&
  a.expirationDate === b.expirationDate &&
  a.cvv === b.cvv &&
  a.billingAddress?.postalCode === b.billingAddress?.postalCode

// Create nonce via Braintree
const createCreditCardNonce = async (creditCard: CreditCardPayload): Promise<string> =>
  braintree.client
    .create({ authorization: CLIENT_AUTHORIZATION })
    .then((client_) =>
      (client_.request as ClientRequest)({
        endpoint: 'payment_methods/credit_cards',
        method: 'post',
        data: { creditCard },
      }),
    )
    .then((response) => {
      const { binData, nonce } = response.creditCards[0]

      if (binData && binData.prepaid == 'Yes') throw 'PaymentErrorPrepaidCard'

      return nonce
    })

// React hook to create a credit card nonce using react-query
const useCreditCardNonce = (params?: {
  onSuccess?: (nonce: string) => void
  onError?: (error: BraintreeError) => void
}): UseMutationResult<string, BraintreeError, CreditCardPayload, unknown> => {
  return useMutation<string, BraintreeError, CreditCardPayload>({
    mutationFn: createCreditCardNonce,
    onSuccess: params?.onSuccess,
    onError: params?.onError,
  })
}

export default useCreditCardNonce
